import React from 'react';
import {
    Dialog,
    DialogContent,
    DialogClose,
} from './ui/dialog';
import { Badge } from './ui/badge';

const ProductDetailModal = ({ isOpen, onClose, product }) => {
    if (!product) return null;

    // Map API field names to component field names
    const productData = {
        ...product,
        flavorNotes: product.flavor_notes || [],
        recommendedBrew: product.recommended_brew || [],
        aftertaste: product.aftertaste || '-'
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-4xl w-[95vw] md:w-full p-0 overflow-hidden bg-white border-none rounded-2xl max-h-[90vh] flex flex-col md:block">

                {/* Close Button - Visible on Mobile/Desktop */}
                <DialogClose className="absolute right-4 top-4 z-50 p-2 bg-white/50 backdrop-blur-sm rounded-full hover:bg-white transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 text-gray-800">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                </DialogClose>

                <div className="grid grid-cols-1 md:grid-cols-2 h-full overflow-y-auto md:overflow-visible">
                    {/* Left Column: Image */}
                    <div className="relative h-[300px] md:h-full bg-gray-100 shrink-0">
                        <img
                            src={productData.image}
                            alt={productData.name}
                            className="absolute inset-0 w-full h-full object-cover"
                        />
                    </div>

                    {/* Right Column: Details */}
                    <div className="p-6 md:p-12 space-y-8 bg-white overflow-y-visible md:max-h-[85vh] md:overflow-y-auto">
                        {/* Title and Description */}
                        <div className="space-y-4 pb-6 border-b border-gray-200">
                            <h2 className="text-2xl md:text-4xl font-bold text-[#31415A] leading-tight pr-8">
                                {productData.name}
                            </h2>
                            <p className="text-gray-600 leading-relaxed text-sm md:text-lg">
                                {productData.description}
                            </p>
                        </div>

                        {/* Product Details */}
                        <div className="space-y-6">
                            {/* Flavor Notes */}
                            <div className="space-y-2">
                                <h3 className="font-semibold text-[#31415A] text-lg">Flavor Notes</h3>
                                <div className="flex flex-wrap gap-2">
                                    {productData.flavorNotes.map((note, index) => (
                                        <Badge
                                            key={index}
                                            variant="secondary"
                                            className="px-3 py-1 text-sm"
                                            style={{ backgroundColor: '#31415A', color: '#ffff' }}
                                        >
                                            {note}
                                        </Badge>
                                    ))}
                                </div>
                            </div>

                            {/* Body & Acidity */}
                            <div className="grid grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <h3 className="font-semibold text-[#31415A] text-lg">Body</h3>
                                    <p className="text-gray-700 text-base">{productData.body}</p>
                                </div>
                                <div className="space-y-2">
                                    <h3 className="font-semibold text-[#31415A] text-lg">Acidity</h3>
                                    <p className="text-gray-700 text-base">{productData.acidity}</p>
                                </div>
                            </div>

                            {/* Aftertaste */}
                            <div className="space-y-2">
                                <h3 className="font-semibold text-[#31415A] text-lg">Aftertaste</h3>
                                <p className="text-gray-700 text-base">{productData.aftertaste}</p>
                            </div>

                            {/* Recommended Brew */}
                            <div className="space-y-2">
                                <h3 className="font-semibold text-[#31415A] text-lg">Recommended Brew</h3>
                                <div className="flex flex-wrap gap-2">
                                    {productData.recommendedBrew.map((method, index) => (
                                        <Badge
                                            key={index}
                                            className="px-3 py-1 text-sm"
                                            style={{ backgroundColor: '#31415A', color: '#ffff' }}
                                        >
                                            {method}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default ProductDetailModal;
